import React from 'react';
import { useNavigate } from 'react-router';
import { useAiServices, useCreateAiService, useToggleAiService } from '@redbax/shared';
import type { AiService } from '@redbax/shared';
import {
  PageHeader,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  EmptyState,
  LoadingSkeleton,
} from '@redbax/ui-kit';
import { Plus, Bot } from 'lucide-react';

const categories = [
  'Chatbot',
  'CodingAssistant',
  'Productivity',
  'ImageMedia',
  'Search',
  'ChineseAI',
  'Other',
] as const;

function categoryBadgeVariant(category: string) {
  switch (category) {
    case 'Chatbot':
      return 'default' as const;
    case 'CodingAssistant':
      return 'secondary' as const;
    case 'Productivity':
      return 'outline' as const;
    case 'ImageMedia':
      return 'warning' as const;
    case 'Search':
      return 'success' as const;
    case 'ChineseAI':
      return 'destructive' as const;
    default:
      return 'outline' as const;
  }
}

function categoryLabel(category: string): string {
  switch (category) {
    case 'CodingAssistant':
      return 'Coding Assistant';
    case 'ImageMedia':
      return 'Image & Media';
    case 'ChineseAI':
      return 'Chinese AI';
    default:
      return category;
  }
}

function groupByCategory(services: AiService[]) {
  const groups: Record<string, AiService[]> = {};
  for (const service of services) {
    const cat = service.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(service);
  }
  return groups;
}

export function AiServicesPage() {
  const navigate = useNavigate();
  const { data: services, isLoading } = useAiServices();
  const createService = useCreateAiService();
  const toggleService = useToggleAiService();

  const [name, setName] = React.useState('');
  const [category, setCategory] = React.useState<string>('Chatbot');
  const [provider, setProvider] = React.useState('');
  const [domainsInput, setDomainsInput] = React.useState('');

  const handleCreate = () => {
    if (!name.trim() || !domainsInput.trim()) return;
    const domains = domainsInput.split(',').map((d) => d.trim()).filter(Boolean);
    if (domains.length === 0) return;
    createService.mutate({ name, category, provider: provider || undefined, domains });
    setName('');
    setCategory('Chatbot');
    setProvider('');
    setDomainsInput('');
  };

  if (isLoading) {
    return (
      <div>
        <PageHeader title="AI Services" description="Manage monitored AI services and their domains" />
        <LoadingSkeleton rows={4} />
      </div>
    );
  }

  const grouped = groupByCategory(services ?? []);

  return (
    <div>
      <PageHeader
        title="AI Services"
        description="Manage monitored AI services and their domains"
        actions={
          <Dialog>
            <DialogTrigger className="inline-flex items-center gap-2 rounded-md bg-red-accent px-4 py-2 text-sm font-medium text-text-primary hover:bg-red-hover">
              <Plus className="h-4 w-4" />
              Add Custom Service
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Custom AI Service</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label htmlFor="service-name">Name</Label>
                  <Input
                    id="service-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Internal AI Tool"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="service-category">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {categoryLabel(cat)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="service-provider">Provider (optional)</Label>
                  <Input
                    id="service-provider"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    placeholder="e.g., Acme Corp"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="service-domains">Domains (comma-separated)</Label>
                  <Input
                    id="service-domains"
                    value={domainsInput}
                    onChange={(e) => setDomainsInput(e.target.value)}
                    placeholder="e.g., api.example.com, chat.example.com"
                    className="mt-1"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={handleCreate} disabled={!name.trim() || !domainsInput.trim()}>
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      {!services || services.length === 0 ? (
        <EmptyState
          icon={<Bot className="h-12 w-12" />}
          title="No AI services configured"
          description="AI services will appear here after database seeding or when you add custom services."
        />
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([cat, catServices]) => (
            <div key={cat}>
              <h2 className="mb-4 text-lg font-semibold text-text-primary">{categoryLabel(cat)}</h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {catServices.map((service) => (
                  <Card
                    key={service.id}
                    className="cursor-pointer transition-colors hover:border-red-accent/50"
                    onClick={() => navigate(`/ai-services/${service.id}`)}
                  >
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{service.name}</CardTitle>
                        <div className="flex items-center gap-2">
                          {service.isBuiltIn && (
                            <Badge variant="outline">Built-in</Badge>
                          )}
                          <Badge variant={categoryBadgeVariant(service.category)}>
                            {categoryLabel(service.category)}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          {service.provider && (
                            <p className="text-sm text-text-secondary">{service.provider}</p>
                          )}
                          <p className="mt-1 text-xs text-text-secondary">
                            {service.domains.length} domain{service.domains.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleService.mutate(service.id);
                          }}
                          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                            service.isEnabled
                              ? 'bg-green-ok/20 text-green-ok'
                              : 'bg-surface-raised text-text-secondary'
                          }`}
                          aria-label={service.isEnabled ? 'Disable service' : 'Enable service'}
                        >
                          {service.isEnabled ? 'Enabled' : 'Disabled'}
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
